import { call, put, select } from "redux-saga/effects";

import DefMembershipActions, { ACTIVE_MEMBERSHIP_STORAGE_KEY } from "../actions/defMembership-actions";
import { getDefMembership, setDefaultMembership } from "../../Service/api";

const isDefaultMembership = (membership) =>
	membership?.isDefault === true || membership?.default === true;

const sameMembershipId = (a, b) => a != null && b != null && String(a) === String(b);

const findMembership = (membershipAccounts, membershipId) =>
	(membershipAccounts || []).find((membership) => sameMembershipId(membership.id, membershipId));

const pickActiveMembership = (memberships) => {
	if (!memberships?.length) {
		return null;
	}
	try {
		const storedId = sessionStorage.getItem(ACTIVE_MEMBERSHIP_STORAGE_KEY);
		if (storedId != null) {
			const match = findMembership(memberships, storedId);
			if (match) {
				return match;
			}
			// Stale id (e.g. after membership revoked) — clear so App bootstrap does not resync-loop
			sessionStorage.removeItem(ACTIVE_MEMBERSHIP_STORAGE_KEY);
		}
	} catch (e) {
		// sessionStorage may be unavailable
	}
	return memberships.find(isDefaultMembership) || memberships[0];
};

const toErrorPayload = (response) => ({
	...(response.data || {}),
	status: response.status,
});

const markDefaultFlags = (membershipAccounts, membershipId) =>
	(membershipAccounts || []).map((membership) => ({
		...membership,
		isDefault: sameMembershipId(membership.id, membershipId),
		default: sameMembershipId(membership.id, membershipId),
	}));

const defaultPathForRole = (role) =>
	["VIEWER", "MANAGER"].includes(role) ? "/crm/customers" : "/home";

// attempts to fetch membership accounts and select the default / active one
export function* getDefMembershipData() {
	const response = yield call(getDefMembership);

	if (response.ok) {
		const membershipAccounts = Array.isArray(response.data) ? response.data : [];
		if (membershipAccounts.length === 0) {
			// No memberships — same UX as the previous 404 from /account/default
			yield put(DefMembershipActions.defMembershipFailure({ status: 404 }));
			return;
		}
		const defMembership = pickActiveMembership(membershipAccounts);
		yield put(DefMembershipActions.defMembershipSuccess(defMembership, membershipAccounts));
		return;
	}

	yield put(DefMembershipActions.defMembershipFailure(toErrorPayload(response)));
}

// Switch the active membership used for fetching data (session only; does not require set-default API)
export function* switchMembershipData(action) {
	const { membershipId } = action;

	// Pause persistence before any further yields so switching:true from REQUEST
	// is less likely to be written to storage after (or instead of) purge.
	let persistor;
	try {
		({ persistor } = yield call(() => import("../store")));
		persistor.pause();
	} catch (e) {
		persistor = null;
	}

	const resumePersist = () => {
		if (persistor) {
			try {
				persistor.persist();
			} catch (e) {
				// ignore
			}
		}
	};

	const membershipAccounts = yield select((state) => state.defMembership.membershipAccounts || []);
	const currentMembership = yield select((state) => state.defMembership.defMembership);
	const selected = findMembership(membershipAccounts, membershipId);

	if (!selected) {
		resumePersist();
		yield put(DefMembershipActions.switchMembershipFailure({ status: 404 }));
		return;
	}

	if (sameMembershipId(currentMembership?.id, membershipId)) {
		resumePersist();
		yield put(DefMembershipActions.switchMembershipSuccess(currentMembership, membershipAccounts));
		return;
	}

	try {
		sessionStorage.setItem(ACTIVE_MEMBERSHIP_STORAGE_KEY, String(membershipId));
	} catch (e) {
		// sessionStorage may be unavailable
	}

	// Clear switching before purge/navigate so a late persist cannot rehydrate switching:true,
	// and so the topbar spinner stops even if the hard reload is delayed.
	yield put(DefMembershipActions.switchMembershipSuccess(selected, membershipAccounts));

	// Clear persisted caches for the previous business, then hard-reload into the new membership context
	try {
		if (persistor) {
			yield call([persistor, persistor.purge]);
		}
	} catch (e) {
		// continue with navigation even if purge fails
	}

	window.location.assign(defaultPathForRole(selected.role));
}

// Persist the current membership as the account default
export function* setDefaultMembershipData(action) {
	const { membershipId } = action;
	const membershipAccounts = yield select((state) => state.defMembership.membershipAccounts || []);
	const currentMembership = yield select((state) => state.defMembership.defMembership);
	const selected = findMembership(membershipAccounts, membershipId);

	if (!selected) {
		yield put(DefMembershipActions.setDefaultMembershipFailure({ status: 404 }));
		return;
	}

	try {
		const response = yield call(setDefaultMembership, membershipId);

		if (response.status === 403) {
			yield put(DefMembershipActions.setDefaultMembershipFailure(toErrorPayload(response)));
			window.location.assign("/denied");
			return;
		}

		if (!response.ok) {
			yield put(DefMembershipActions.setDefaultMembershipFailure(toErrorPayload(response)));
			return;
		}

		const updatedAccounts = markDefaultFlags(membershipAccounts, membershipId);
		const defMembership = response.data
			|| (sameMembershipId(currentMembership?.id, membershipId)
				? { ...currentMembership, isDefault: true, default: true }
				: { ...selected, isDefault: true, default: true });

		try {
			sessionStorage.setItem(ACTIVE_MEMBERSHIP_STORAGE_KEY, String(membershipId));
		} catch (e) {
			// sessionStorage may be unavailable
		}

		yield put(DefMembershipActions.setDefaultMembershipSuccess(defMembership, updatedAccounts));
	} catch (e) {
		yield put(DefMembershipActions.setDefaultMembershipFailure({ status: 500, message: e?.message }));
	}
}
